import { createApi } from '@reduxjs/toolkit/query/react';
import { idType, selectItemType } from '@src/types';
import { baseQuerySample } from '@store/helpers';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';

export type companySettingsType = {
  scheduledPickupEarlyToleranceMinutes: number;
  scheduledPickupLateToleranceMinutes: number;
  willCallResponseWindowMinutes: number;
};

type getPayorRatesByIdResType = {
  id: number;
  effectiveDate: string;
  expirationDate: string;
  contractDocumentId: number;
  hcpcOrBillingCode: string;
  bucketStartUnits: number;
  bucketEndUnits: number;
  bucketFreeUnits: number;
  baseRate: number;
  unitRate: number;
  markUpValue: number;
  modeOfTransport: selectItemType | null;
  equipmentAndAddon: selectItemType | null;
  lob: selectItemType | null;
  markUpType: selectItemType | null;
  billQualifierType: selectItemType | null;
};

export const payorsApi = createApi({
  reducerPath: 'Payors',
  tagTypes: ['Payors'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getPayors: builder.query({
      query: () => ({
        url: '/v1/company',
      }),
    }),

    getPayorTypes: builder.query({
      query: () => ({
        url: '/v1/company/types',
      }),
    }),
    getPayorRates: builder.mutation({
      query: ({ body = {}, params = {} }) => ({
        url: `/v1/company/rate`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getPayorRatesById: builder.query<getPayorRatesByIdResType, { id?: idType }>(
      {
        query: ({ id }) => ({
          url: `/v1/company/rate/${id}`,
        }),
      }
    ),
    createOrUpdatePayors: builder.mutation({
      query: ({ id, body, method }) => ({
        url: `/v1/company/rate/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),

    // TODO: use right endpoint to get data
    getExcludedContractors: builder.query({
      // query: ({}) => '/',
      query: ({
        id,
        page,
        size,
        body = {},
        sorting = { sortBy: 'expiration', sortDir: 'desc' },
      }) => {
        return {
          url: '/',
          method: 'GET',
          body,
          params: sorting,
        };
      },
    }),

    // TODO: use right endpoint to get data
    getExcludedContractorById: builder.query({
      query: () => ({
        url: '/',
      }),
    }),

    // TODO: use right endpoint to create or update  contractor
    createExcludedContractor: builder.mutation({
      query: ({ body, url, method }) => {
        return {
          url,
          method,
          body,
        };
      },
    }),
    createOrUpdatePayorInfo: builder.mutation({
      query: ({ body, isPayorSaved }) => ({
        url: `/v1/company/${isPayorSaved ? 'update' : 'create'}`,
        method: isPayorSaved ? 'PUT' : 'POST',
        body,
      }),
    }),
    getLevelOfService: builder.mutation({
      query: (body) => ({
        url: `/v1/search/payer/get-level-of-service`,
        method: 'POST',
        body,
      }),
    }),
    updatePayorInfoTags: builder.mutation({
      query: ({ body, url, method }) => {
        return {
          url,
          method,
          body,
        };
      },
    }),
    payerBasicAnalytics: builder.query({
      query: ({ query }) => ({
        url: `/v1/search/payer/basic-analytics?time-frame=${query}`,
      }),
    }),
    getLobLimit: builder.query({
      query: () => `/v1/company/lob/limit`,
    }),
    toggleMobile: builder.query({
      query: (isActive: boolean) =>
        `/v1/company/info/change-mobile/${isActive}`,
    }),
    getMobileState: builder.query({
      query: () => `/v1/company/info/change-mobile`,
    }),
    toggleLobById: builder.query({
      query: (lobId: number) => `/v1/company/info/line-of-business/${lobId}`,
    }),
    getCanCreateRideStatus: builder.query({
      query: () => `/v1/company/info/create-ride`,
    }),
    updateCanCreateRide: builder.query({
      query: (isActive: boolean) => `/v1/company/info/can-create-ride/${isActive}`,
    }),
    getCompanySettings: builder.query<companySettingsType, void>({
      query: () => `/v1/company/settings`,
    }),
    updateCompanySettings: builder.mutation<
      companySettingsType,
      Partial<companySettingsType>
    >({
      query: (body) => ({
        url: `/v1/company/settings`,
        method: 'PUT',
        body,
      }),
    }),
  }),
});

export const {
  useGetCanCreateRideStatusQuery,
  useLazyUpdateCanCreateRideQuery,
  usePayerBasicAnalyticsQuery,
  useLazyPayerBasicAnalyticsQuery,
  useGetPayorRatesMutation,
  useLazyGetPayorsQuery,
  useLazyGetPayorRatesByIdQuery,
  useCreateOrUpdatePayorsMutation,
  useLazyGetPayorTypesQuery,
  useLazyGetExcludedContractorByIdQuery,
  useLazyGetExcludedContractorsQuery,
  useCreateExcludedContractorMutation,
  useUpdatePayorInfoTagsMutation,
  useCreateOrUpdatePayorInfoMutation,
  useGetLevelOfServiceMutation,
  useLazyGetLobLimitQuery,
  useGetLobLimitQuery,
  useLazyToggleMobileQuery,
  useLazyGetMobileStateQuery,
  useGetMobileStateQuery,
  useLazyToggleLobByIdQuery,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingsMutation,
} = payorsApi;
